import { createWorkspace, emptyFog } from "./defaults";
import { repairCroppedGeometry } from "./dd2vtt";
import { readLocalAssetFallback } from "./local-asset-fallback";
import { createPlayerSnapshot } from "./player-snapshot";
import { isPlayableMapScene } from "./media-map";
import { presentationIsFrozen } from "./presentation";
import type { AppSettings, Asset, CalibrationProfile, DiceRollState, DisplayViewportMetrics, PresentationSnapshot, Scene, TableEdge, WorkspaceState } from "../types";
import { randomUuid } from "./browser-crypto";
import { BUNDLED_MEDIA_DIRECTORY } from "./bundled-media";
import { mediaUrl } from "./media-url";

type Listener = (state: WorkspaceState) => void;
type DbResponse = { id: string; result?: unknown; error?: string; storageMode?: string };
type ChannelMessage =
  | { type: "workspace-request"; requestId: string }
  | { type: "workspace-response"; requestId: string; state: WorkspaceState }
  | { type: "commit-request"; requestId: string; state: WorkspaceState }
  | { type: "commit-response"; requestId: string; state?: WorkspaceState; error?: string }
  | { type: "workspace-updated"; state: WorkspaceState }
  | { type: "presentation"; snapshot: PresentationSnapshot }
  | { type: "cover-effect"; covered: boolean; dmTableEdge?: TableEdge; coverColor?: string }
  | { type: "dice-effect"; diceRoll?: DiceRollState }
  | { type: "calibration-preview"; pixelsPerInch?: number; calibration?: CalibrationProfile }
  | { type: "display-metrics"; metrics: DisplayViewportMetrics }
  | { type: "preload-ack"; revision: number; sceneId?: string; preloadKey?: string; ready?: boolean; error?: string }
  | { type: "cue-preload"; requestId: string; snapshots: PresentationSnapshot[] }
  | { type: "cue-preload-ack"; requestId: string; error?: string }
  | { type: "display-probe" }
  | { type: "display-status"; connected: boolean };

const SAFE_SNAPSHOT_KEY = "tbltop-player-snapshot";

/**
 * The player snapshot is only a recovery/cache hint. It can be much larger
 * than the small localStorage quota on geometry-heavy campaigns, so failure
 * to cache it must never prevent the database owner from starting.
 */
function cacheSafeSnapshot(snapshot: PresentationSnapshot) {
  try {
    localStorage.setItem(SAFE_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch (error) {
    // Remove an older oversized value so subsequent attempts do not keep
    // failing. BroadcastChannel remains the live synchronization path.
    try { localStorage.removeItem(SAFE_SNAPSHOT_KEY); } catch { /* storage unavailable */ }
    console.warn("TBLTOP could not cache the player recovery snapshot.", error);
  }
}

/** Asset readiness is independent from unrelated presentation revision changes. */
export function preloadIdentity(snapshot: PresentationSnapshot) {
  const assets = snapshot.assets.map((asset) => `${asset.id}:${asset.opfsPath}:${asset.mime}`).sort().join("|");
  return `${snapshot.scene?.id ?? "none"}::${assets}`;
}

async function decodeImageElement(source: Blob, name: string) {
  const url = URL.createObjectURL(source);
  const image = new Image();
  let timeout: number | undefined;
  try {
    await Promise.race([
      new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error(`${name} could not be decoded by this browser.`));
        image.decoding = "async";
        image.src = url;
      }),
      new Promise<never>((_, reject) => { timeout = window.setTimeout(() => reject(new Error(`${name} took too long to prepare.`)), 30_000); }),
    ]);
  } finally {
    if (timeout) clearTimeout(timeout);
    image.src = "";
    URL.revokeObjectURL(url);
  }
}

/**
 * `createImageBitmap` is the fastest raster preloader, but Safari and some
 * Chromium builds reject otherwise valid SVG blobs with "source image could
 * not be decoded". Keep the declared MIME type after OPFS/backup restoration
 * and use the universally supported image-element decoder for SVGs. The same
 * decoder is a compatibility fallback for raster codecs implemented by the
 * browser's image element but not by `createImageBitmap`.
 */
export async function preloadImageAsset(file: Blob, mime: string, name: string) {
  const source = file.type === mime ? file : new Blob([file], { type: mime });
  if (mime === "image/svg+xml" || typeof createImageBitmap !== "function") {
    await decodeImageElement(source, name);
    return;
  }
  const decode = createImageBitmap(source);
  let timeout: number | undefined;
  try {
    const bitmap = await Promise.race([
      decode,
      new Promise<never>((_, reject) => { timeout = window.setTimeout(() => reject(new Error(`${name} took too long to prepare.`)), 30_000); }),
    ]);
    bitmap.close();
  } catch (error) {
    void decode.then((bitmap) => bitmap.close(), () => undefined);
    if (error instanceof Error && error.message === `${name} took too long to prepare.`) throw error;
    await decodeImageElement(source, name);
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

export class WorkspaceClient {
  private channel = new BroadcastChannel("tbltop-workspace-v1");
  private worker?: Worker;
  private owner = false;
  private state?: WorkspaceState;
  private listeners = new Set<Listener>();
  private pending = new Map<string, { resolve: (value: unknown) => void; reject: (error: Error) => void }>();
  private releaseOwner?: () => void;
  private displayMonitor?: number;
  private lastDisplaySeen = 0;
  private displayStatusCommit = false;
  // Display heartbeats and viewport telemetry are background writes. Serialize
  // them with foreground edits so a delayed telemetry save cannot replace a
  // just-staged scene with an older presentation snapshot.
  private ownerCommitQueue: Promise<void> = Promise.resolve();
  private displayMetricsTimer?: number;
  private pendingDisplayMetrics?: DisplayViewportMetrics;
  private closed = false;
  private ownerStartupError?: Error;
  private closing?: Promise<void>;
  storageMode = "starting";

  constructor() {
    this.channel.onmessage = (event: MessageEvent<ChannelMessage>) => void this.onChannel(event.data);
  }

  async start(): Promise<WorkspaceState> {
    // A refresh can briefly overlap the previous document's pagehide while
    // its database-owner lock is being released. Keep retrying the bounded
    // BroadcastChannel request during that handoff instead of leaving the DM
    // console on an infinite "Opening local campaign library" spinner.
    let lastError: unknown;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        await this.electOwner();
        if (this.owner) { this.startDisplayMonitor(); return this.state!; }
        return await this.requestWorkspace();
      }
      catch (error) {
        lastError = error;
        if (attempt < 3) await new Promise((resolve) => window.setTimeout(resolve, 180 + attempt * 170));
      }
    }
    throw new Error(`The local campaign library did not respond after refresh${lastError instanceof Error ? `: ${lastError.message}` : "."}`);
  }

  private startDisplayMonitor() {
    if (!this.owner || this.displayMonitor) return;
    this.lastDisplaySeen = Date.now();
    const check = () => {
      if (this.closed || !this.state) return;
      this.channel.postMessage({ type: "display-probe" } satisfies ChannelMessage);
      if (this.state.presentation.displayConnected && Date.now() - this.lastDisplaySeen > 3_500 && !this.displayStatusCommit) {
        this.displayStatusCommit = true;
        void this.commitBackgroundPatch((current) => current.presentation.displayConnected ? { ...current, presentation: { ...current.presentation, displayConnected: false } } : undefined).finally(() => { this.displayStatusCommit = false; });
      }
    };
    check(); this.displayMonitor = window.setInterval(check, 1_500);
  }

  private async electOwner() {
    if (!navigator.locks) { await this.becomeOwner(); return; }
    await new Promise<void>((resolve) => {
      void navigator.locks.request("tbltop-database-owner", { ifAvailable: true }, async (lock) => {
        if (!lock) { resolve(); return; }
        try {
          await this.becomeOwner();
          resolve();
          await new Promise<void>((release) => { this.releaseOwner = release; });
        } catch (error) {
          this.ownerStartupError = error instanceof Error ? error : new Error("The local database could not start.");
          this.owner = false;
          this.releaseOwner = undefined;
          resolve();
        }
      });
    });
    if (this.ownerStartupError) {
      const error = this.ownerStartupError;
      this.ownerStartupError = undefined;
      throw error;
    }
  }

  private async becomeOwner() {
    if (this.closed) return;
    this.owner = true;
    this.worker = new Worker(new URL("../workers/database.worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (event: MessageEvent<DbResponse>) => {
      const pending = this.pending.get(event.data.id);
      if (!pending) return;
      this.pending.delete(event.data.id);
      if (event.data.storageMode) this.storageMode = event.data.storageMode;
      if (event.data.error) pending.reject(new Error(event.data.error)); else pending.resolve(event.data.result);
    };
    try {
      const loaded = await this.dbRequest<WorkspaceState | null>("init", undefined, 12_000);
      this.state = loaded ?? createWorkspace();
      if (!loaded) this.state = await this.dbRequest<WorkspaceState>("commit", this.state);
      else {
        let repaired = false;
        const scenes = this.state.scenes.map((scene) => {
          if (!scene.geometry) return scene;
          const result = repairCroppedGeometry(scene.geometry);
          if (!result.changed) return scene;
          repaired = true;
          return { ...scene, geometry: result.geometry, updatedAt: Date.now() };
        });
        if (repaired) this.state = await this.dbRequest<WorkspaceState>("commit", { ...this.state, scenes });
      }
      if (!this.closed) this.publish(this.state);
    } catch (error) {
      this.worker?.terminate();
      this.worker = undefined;
      this.owner = false;
      throw error;
    }
  }

  private dbRequest<T>(type: "init" | "commit" | "export" | "optimize" | "close", state?: WorkspaceState, timeoutMs = 30_000): Promise<T> {
    if (!this.worker) return Promise.reject(new Error("Database owner is unavailable."));
    const id = randomUuid();
    return new Promise<T>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error("The local database did not respond. Close other TBLTOP tabs and try again."));
      }, timeoutMs);
      this.pending.set(id, {
        resolve: (value) => { window.clearTimeout(timeout); resolve(value as T); },
        reject: (error) => { window.clearTimeout(timeout); reject(error); },
      });
      this.worker!.postMessage({ id, type, state });
    });
  }

  private async commitWithRetry(state: WorkspaceState): Promise<WorkspaceState> {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try { return await this.dbRequest<WorkspaceState>("commit", state); }
      catch (error) {
        if (attempt === 2) throw new Error(`Could not save the library database: ${error instanceof Error ? error.message : "Unknown database error."}`);
        await new Promise((resolve) => window.setTimeout(resolve, 75 * (attempt + 1)));
      }
    }
    throw new Error("Workspace commit failed.");
  }

  private enqueueOwnerCommit<T>(work: () => Promise<T>): Promise<T> {
    const queued = this.ownerCommitQueue.then(work, work);
    this.ownerCommitQueue = queued.then(() => undefined, () => undefined);
    return queued;
  }

  private async commitAsOwner(state: WorkspaceState): Promise<WorkspaceState> {
    const committed = await this.commitWithRetry(state);
    this.publish(committed);
    return committed;
  }

  /** Rebase a background-only patch immediately before it is persisted. */
  private commitBackgroundPatch(patch: (current: WorkspaceState) => WorkspaceState | undefined): Promise<WorkspaceState | undefined> {
    if (!this.owner) return Promise.resolve(undefined);
    return this.enqueueOwnerCommit(async () => {
      const current = this.state;
      if (!current) return undefined;
      const next = patch(current);
      return next ? this.commitAsOwner(next) : undefined;
    });
  }

  private requestWorkspace(): Promise<WorkspaceState> {
    const requestId = randomUuid();
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => { this.pending.delete(requestId); reject(new Error("The DM database window did not respond.")); }, 1200);
      this.pending.set(requestId, {
        resolve: (value) => { clearTimeout(timeout); this.state = value as WorkspaceState; resolve(value as WorkspaceState); },
        reject,
      });
      this.channel.postMessage({ type: "workspace-request", requestId } satisfies ChannelMessage);
    });
  }

  private async onChannel(message: ChannelMessage) {
    if (message.type === "workspace-request" && this.owner && this.state) {
      this.channel.postMessage({ type: "workspace-response", requestId: message.requestId, state: this.state } satisfies ChannelMessage);
    } else if (message.type === "workspace-response") {
      this.pending.get(message.requestId)?.resolve(message.state); this.pending.delete(message.requestId);
    } else if (message.type === "commit-request" && this.owner) {
      try {
        const state = await this.enqueueOwnerCommit(() => this.commitAsOwner(message.state));
        this.channel.postMessage({ type: "commit-response", requestId: message.requestId, state } satisfies ChannelMessage);
      } catch (error) {
        this.channel.postMessage({ type: "commit-response", requestId: message.requestId, error: error instanceof Error ? error.message : "Commit failed." } satisfies ChannelMessage);
      }
    } else if (message.type === "commit-response") {
      const pending = this.pending.get(message.requestId); if (!pending) return;
      this.pending.delete(message.requestId);
      if (message.error) pending.reject(new Error(message.error)); else pending.resolve(message.state);
    } else if (message.type === "workspace-updated") {
      this.state = message.state; this.emit();
    } else if (message.type === "preload-ack" && this.owner && this.state) {
      await this.commitBackgroundPatch((current) => {
        const stagedSceneId = current.presentation.stagedSceneId;
        const currentStage = stagedSceneId && (!message.sceneId || message.sceneId === stagedSceneId);
        const currentPreload = createPlayerSnapshot(current, true);
        const currentPreloadKey = preloadIdentity(currentPreload);
        const currentSnapshot = message.preloadKey ? message.preloadKey === currentPreloadKey : message.revision === current.presentation.revision;
        if (!currentStage || !currentSnapshot) return undefined;
        const ready = message.ready !== false;
        return { ...current, presentation: { ...current.presentation, stagedReady: ready, stagingError: ready ? undefined : (message.error || "The Player Display could not prepare this scene."), displayConnected: true } };
      });
    } else if (message.type === "cue-preload-ack") {
      const pending = this.pending.get(message.requestId); if (!pending) return;
      this.pending.delete(message.requestId);
      if (message.error) pending.reject(new Error(message.error)); else pending.resolve(true);
    } else if (message.type === "display-status" && this.owner && this.state) {
      if (message.connected) this.lastDisplaySeen = Date.now();
      if (this.state.presentation.displayConnected === message.connected) {
        if (message.connected && this.state.presentation.stagedSceneId && !this.state.presentation.stagedReady) this.stagePreload(this.state);
        return;
      }
      const updated = await this.commitBackgroundPatch((current) => current.presentation.displayConnected === message.connected ? undefined : { ...current, presentation: { ...current.presentation, displayConnected: message.connected } });
      if (message.connected && updated?.presentation.stagedSceneId && !updated.presentation.stagedReady) this.stagePreload(updated);
    } else if (message.type === "display-metrics" && this.owner && this.state) {
      this.pendingDisplayMetrics = message.metrics;
      if (this.displayMetricsTimer) clearTimeout(this.displayMetricsTimer);
      this.displayMetricsTimer = window.setTimeout(() => {
        this.displayMetricsTimer = undefined;
        const metrics = this.pendingDisplayMetrics; this.pendingDisplayMetrics = undefined;
        if (!metrics) return;
        void this.commitBackgroundPatch((current) => {
          const previous = current.settings.displayViewport;
          if (previous && previous.width === metrics.width && previous.height === metrics.height && previous.devicePixelRatio === metrics.devicePixelRatio && previous.fullscreen === metrics.fullscreen) return undefined;
          return { ...current, settings: { ...current.settings, displayViewport: metrics } };
        });
      }, 120);
    }
  }

  private publish(state: WorkspaceState) {
    if (this.closed) return;
    this.state = state;
    this.channel.postMessage({ type: "workspace-updated", state } satisfies ChannelMessage);
    if (this.owner && state.presentation.displayConnected && state.presentation.stagedSceneId && !state.presentation.stagedReady && !state.presentation.stagingError) this.stagePreload(state);
    if (!presentationIsFrozen(state.presentation)) {
      const snapshot = createPlayerSnapshot(state);
      cacheSafeSnapshot(snapshot);
      this.channel.postMessage({ type: "presentation", snapshot } satisfies ChannelMessage);
    }
    this.emit();
  }

  private emit() { if (this.state) for (const listener of this.listeners) listener(this.state); }

  subscribe(listener: Listener) { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }

  async commit(state: WorkspaceState): Promise<WorkspaceState> {
    if (this.owner) {
      return this.enqueueOwnerCommit(() => this.commitAsOwner(state));
    }
    const requestId = randomUuid();
    return new Promise((resolve, reject) => {
      this.pending.set(requestId, { resolve: resolve as (value: unknown) => void, reject });
      this.channel.postMessage({ type: "commit-request", requestId, state } satisfies ChannelMessage);
    });
  }

  async commitSettings(settings: AppSettings): Promise<WorkspaceState> {
    if (!this.state) throw new Error("Workspace is unavailable.");
    return this.commit({ ...this.state, settings });
  }

  async appendImportedAssets(assets: Asset[], scene?: Scene): Promise<WorkspaceState> {
    if (!this.state) throw new Error("Workspace is unavailable.");
    return this.commit({ ...this.state, assets: [...this.state.assets, ...assets], scenes: scene ? [...this.state.scenes, scene] : this.state.scenes });
  }

  async exportDatabase(): Promise<Uint8Array> {
    if (!this.owner) throw new Error("Open the primary DM window to export the database.");
    return this.dbRequest<Uint8Array>("export");
  }

  stagePreload(state: WorkspaceState) {
    const snapshot = createPlayerSnapshot(state, true);
    this.channel.postMessage({ type: "presentation", snapshot } satisfies ChannelMessage);
  }

  preloadCueScenes(state: WorkspaceState, sceneIds: string[]): Promise<void> {
    const snapshots = [...new Set(sceneIds)].map((sceneId) => {
      const target = state.scenes.find((scene) => scene.id === sceneId);
      if (!target) throw new Error("A cue scene is no longer available.");
      const presentation = isPlayableMapScene(target)
        ? { ...state.presentation, stagedSceneId: target.id }
        : { ...state.presentation, stagedSceneId: target.id };
      return createPlayerSnapshot({ ...state, presentation }, true);
    });
    if (!snapshots.length) return Promise.resolve();
    const requestId = randomUuid();
    return new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => { this.pending.delete(requestId); reject(new Error("The Player Display did not finish preparing the cue sequence.")); }, 45_000);
      this.pending.set(requestId, { resolve: () => { clearTimeout(timeout); resolve(); }, reject: (error) => { clearTimeout(timeout); reject(error); } });
      this.channel.postMessage({ type: "cue-preload", requestId, snapshots } satisfies ChannelMessage);
    });
  }

  broadcastDiceEffect(diceRoll?: DiceRollState) {
    this.channel.postMessage({ type: "dice-effect", diceRoll } satisfies ChannelMessage);
  }

  broadcastCoverEffect(covered: boolean, dmTableEdge?: TableEdge, coverColor?: string) {
    this.channel.postMessage({ type: "cover-effect", covered, dmTableEdge, coverColor } satisfies ChannelMessage);
  }

  /** Emergency presentation command: resets only the fog on the currently shown map, even while that same scene is staged. */
  broadcastFogReset(sceneId: string, revision: number) {
    let saved: string | null = null;
    try { saved = localStorage.getItem(SAFE_SNAPSHOT_KEY); } catch { return; }
    if (!saved) return;
    try {
      const current = JSON.parse(saved) as PresentationSnapshot;
      if (!current.scene || current.scene.id !== sceneId || !isPlayableMapScene(current.scene)) return;
      const snapshot = { ...current, revision: Math.max(current.revision, revision), scene: { ...current.scene, fog: emptyFog() }, generatedAt: Date.now() };
      cacheSafeSnapshot(snapshot);
      // A standard presentation event also updates Player windows that were
      // opened before this command existed, without exposing staged edits.
      this.channel.postMessage({ type: "presentation", snapshot } satisfies ChannelMessage);
    } catch { /* a future complete snapshot will replace malformed cache data */ }
  }

  broadcastCalibrationPreview(pixelsPerInch?: number, calibration?: CalibrationProfile) {
    this.channel.postMessage({ type: "calibration-preview", pixelsPerInch, calibration } satisfies ChannelMessage);
  }

  private finishClose() {
    if (this.closed) return;
    this.closed = true;
    this.owner = false;
    if (this.displayMonitor) clearInterval(this.displayMonitor);
    if (this.displayMetricsTimer) clearTimeout(this.displayMetricsTimer);
    this.releaseOwner?.();
    this.worker?.terminate();
    for (const pending of this.pending.values()) pending.reject(new Error("Workspace closed."));
    this.pending.clear();
    this.channel.close();
  }

  /** Lets SQLite release its OPFS handles before another route or refreshed page becomes database owner. */
  closeGracefully(): Promise<void> {
    if (this.closed) return Promise.resolve();
    if (this.closing) return this.closing;
    if (!this.owner || !this.worker) {
      this.finishClose();
      return Promise.resolve();
    }
    this.closing = this.dbRequest<boolean>("close", undefined, 2_000)
      .catch(() => false)
      .then(() => { this.finishClose(); });
    return this.closing;
  }

  close() {
    void this.closeGracefully();
  }
}

export class DisplayChannel {
  private channel = new BroadcastChannel("tbltop-workspace-v1");
  private listeners = new Set<(snapshot: PresentationSnapshot) => void>();
  private current?: PresentationSnapshot;
  private calibrationPreviewPixelsPerInch?: number;
  private preloadJobs = new Map<string, Promise<void>>();
  private heartbeat?: number;
  private lastMetrics = "";
  private sendMetrics = () => {
    if (this.closed) return;
    const metrics: DisplayViewportMetrics = {
      width: Math.max(1, Math.round(document.documentElement.clientWidth || window.innerWidth)),
      height: Math.max(1, Math.round(document.documentElement.clientHeight || window.innerHeight)),
      devicePixelRatio: window.devicePixelRatio || 1,
      fullscreen: Boolean(document.fullscreenElement),
    };
    const signature = JSON.stringify(metrics); if (signature === this.lastMetrics) return; this.lastMetrics = signature;
    this.channel.postMessage({ type: "display-metrics", metrics } satisfies ChannelMessage);
  };
  private closed = false;

  constructor() {
    let saved: string | null = null;
    try { saved = localStorage.getItem(SAFE_SNAPSHOT_KEY); } catch { /* recovery cache is optional */ }
    if (saved) { try { this.current = JSON.parse(saved) as PresentationSnapshot; } catch { /* ignore */ } }
    this.channel.onmessage = (event: MessageEvent<ChannelMessage>) => {
      if (event.data.type === "display-probe") {
        if (!this.closed) this.channel.postMessage({ type: "display-status", connected: true } satisfies ChannelMessage);
        return;
      }
      if (event.data.type === "calibration-preview") {
        this.calibrationPreviewPixelsPerInch = event.data.pixelsPerInch;
        if (!this.current) return;
        this.current = {
          ...this.current,
          calibration: event.data.calibration ?? this.current.calibration,
          calibrationPreviewPixelsPerInch: event.data.pixelsPerInch,
        };
        for (const listener of this.listeners) listener(this.current);
        return;
      }
      if (event.data.type === "dice-effect") {
        if (!this.current) return;
        this.current = { ...this.current, diceRoll: event.data.diceRoll };
        cacheSafeSnapshot(this.current);
        for (const listener of this.listeners) listener(this.current);
        return;
      }
      if (event.data.type === "cover-effect") {
        if (!this.current) return;
        this.current = { ...this.current, covered: event.data.covered, dmTableEdge: event.data.dmTableEdge ?? this.current.dmTableEdge, coverColor: event.data.coverColor ?? this.current.coverColor };
        cacheSafeSnapshot(this.current);
        for (const listener of this.listeners) listener(this.current);
        return;
      }
      if (event.data.type === "cue-preload") {
        const requestId = event.data.requestId;
        void Promise.all(event.data.snapshots.map((snapshot) => this.preload(snapshot))).then(
          () => this.channel.postMessage({ type: "cue-preload-ack", requestId } satisfies ChannelMessage),
          (reason) => this.channel.postMessage({ type: "cue-preload-ack", requestId, error: reason instanceof Error ? reason.message : "Cue preload failed." } satisfies ChannelMessage),
        );
        return;
      }
      if (event.data.type !== "presentation") return;
      const snapshot = event.data.snapshot;
      if (this.current && snapshot.revision < this.current.revision) return;
      if (snapshot.preload) {
        const preloadKey = preloadIdentity(snapshot);
        if (this.preloadJobs.has(preloadKey)) return;
        const job = this.preload(snapshot);
        this.preloadJobs.set(preloadKey, job);
        void job.then(
          () => this.channel.postMessage({ type: "preload-ack", revision: snapshot.revision, sceneId: snapshot.scene?.id, preloadKey, ready: true } satisfies ChannelMessage),
          (reason) => this.channel.postMessage({ type: "preload-ack", revision: snapshot.revision, sceneId: snapshot.scene?.id, preloadKey, ready: false, error: reason instanceof Error ? reason.message : "Scene preload failed." } satisfies ChannelMessage),
        ).finally(() => this.preloadJobs.delete(preloadKey));
        return;
      }
      this.current = {
        ...snapshot,
        calibrationPreviewPixelsPerInch: this.calibrationPreviewPixelsPerInch,
      };
      cacheSafeSnapshot(this.current);
      for (const listener of this.listeners) listener(this.current);
    };
    this.channel.postMessage({ type: "display-status", connected: true } satisfies ChannelMessage);
    window.addEventListener("resize", this.sendMetrics); document.addEventListener("fullscreenchange", this.sendMetrics); window.visualViewport?.addEventListener("resize", this.sendMetrics);
    window.setTimeout(this.sendMetrics, 0);
    this.heartbeat = window.setInterval(() => this.channel.postMessage({ type: "display-status", connected: true } satisfies ChannelMessage), 1_000);
  }

  private async preload(snapshot: PresentationSnapshot) {
    if (!snapshot.scene) throw new Error("The staged scene is no longer available.");
    await Promise.all(snapshot.assets.map(async (asset) => {
      const file = await readOpfsFile(asset.opfsPath);
      if (!file) throw new Error(`${asset.name} is missing from local storage.`);
      if (asset.mime.startsWith("image/")) await preloadImageAsset(file, asset.mime, asset.name);
    }));
  }

  subscribe(listener: (snapshot: PresentationSnapshot) => void) {
    this.listeners.add(listener); if (this.current) listener(this.current);
    return () => { this.listeners.delete(listener); };
  }

  setConnected(connected: boolean) {
    if (!this.closed) this.channel.postMessage({ type: "display-status", connected } satisfies ChannelMessage);
  }
  close() {
    if (this.closed) return;
    this.setConnected(false);
    this.closed = true;
    if (this.heartbeat) clearInterval(this.heartbeat);
    window.removeEventListener("resize", this.sendMetrics); document.removeEventListener("fullscreenchange", this.sendMetrics); window.visualViewport?.removeEventListener("resize", this.sendMetrics);
    this.channel.close();
  }
}

export async function readOpfsFile(path: string): Promise<File | null> {
  try {
    const root = await navigator.storage.getDirectory();
    const parts = path.split("/").filter(Boolean);
    let directory = root;
    for (const part of parts.slice(0, -1)) directory = await directory.getDirectoryHandle(part);
    return await (await directory.getFileHandle(parts.at(-1)!)).getFile();
  } catch {
    const fallback = await readLocalAssetFallback(path);
    if (fallback) return fallback;
    if (!path.startsWith(`${BUNDLED_MEDIA_DIRECTORY}/`)) return null;
    const fileName = path.slice(`${BUNDLED_MEDIA_DIRECTORY}/`.length);
    if (!fileName || fileName.includes("/") || fileName.includes("\\") || fileName.includes("..")) return null;
    try {
      const response = await fetch(mediaUrl(`/default-media/files/${encodeURIComponent(fileName)}`));
      if (!response.ok) return null;
      const blob = await response.blob();
      const file = new File([blob], fileName, { type: blob.type || "application/octet-stream" });
      // Cache a bundled track only after it is used. This keeps first launch
      // quick while making played tracks available offline thereafter.
      try {
        const root = await navigator.storage.getDirectory();
        const directory = await root.getDirectoryHandle(BUNDLED_MEDIA_DIRECTORY, { create: true });
        const handle = await directory.getFileHandle(fileName, { create: true });
        const writable = await handle.createWritable();
        try { await writable.write(file); } finally { await writable.close(); }
      } catch { /* direct same-origin playback still works without a cache */ }
      return file;
    } catch { return null; }
  }
}
